// /home/labdoo/zitech-nexus/frontend/src/pages/ServiceRequestsList.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { serviceAPI } from '../services/api';
import './ServiceRequestsList.css';

// SVG Icons
const Icons = {
  Plus: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 5V19M5 12H19" />
    </svg>
  ),
  ArrowRight: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  ),
  Empty: () => (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M8 2V6M16 2V6M3 10H21" />
    </svg>
  ),
  Spinner: () => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="srl-spinner-svg">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  StatusDot: () => (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor">
      <circle cx="4" cy="4" r="4" />
    </svg>
  ),
  Calendar: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Service: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
};

interface ServiceRequest {
  id: number;
  service_name: string;
  service_name_display?: string;
  status: string;
  created_at: string;
  project_details: string;
  company_name?: string;
}

const ServiceRequestsList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await serviceAPI.getMyServiceRequests();
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      pending: '#f59e0b',
      in_review: '#0ea5e9',
      approved: '#10b981',
      in_progress: '#0ea5e9',
      completed: '#10b981',
      cancelled: '#ef4444',
    };
    return colors[status] || '#6b7280';
  };

  const getStatusBackground = (status: string): string => {
    const colors: Record<string, string> = {
      pending: '#fef3c7',
      in_review: '#dbeafe',
      approved: '#d1fae5',
      in_progress: '#dbeafe',
      completed: '#d1fae5',
      cancelled: '#fee2e2',
    };
    return colors[status] || '#f3f4f6';
  };

  const getStatusText = (status: string): string => {
    const texts: Record<string, string> = {
      pending: 'Pending',
      in_review: 'In Review',
      approved: 'Approved',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
    };
    return texts[status] || status;
  };

  const getServiceDisplayName = (request: ServiceRequest): string => {
    if (request.service_name_display) {
      return request.service_name_display;
    }
    if (request.service_name) {
      return request.service_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    return 'Unknown Service';
  };

  const filteredRequests = filter === 'all' 
    ? requests 
    : requests.filter(r => r.status === filter);

  const counts = {
    all: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    in_progress: requests.filter(r => r.status === 'in_progress' || r.status === 'in_review').length,
    completed: requests.filter(r => r.status === 'completed').length,
    cancelled: requests.filter(r => r.status === 'cancelled').length,
  };

  if (loading) {
    return (
      <div className="srl-loading-container">
        <div className="srl-spinner">
          <Icons.Spinner />
        </div>
        <p className="srl-loading-text">Loading your requests...</p>
      </div>
    );
  }

  return (
    <div className="srl-container">
      {/* Header */}
      <div className="srl-header">
        <div className="srl-header-left">
          <h1 className="srl-title">My Service Requests</h1>
          <p className="srl-subtitle">Track and manage your service requests</p>
        </div>
        <button 
          className="srl-btn-primary" 
          onClick={() => navigate('/dashboard/request-service')}
        >
          <Icons.Plus />
          New Request
        </button>
      </div>

      {/* Stats Bar */}
      <div className="srl-stats-bar">
        <div className="srl-stat-item">
          <span className="srl-stat-label">Total</span>
          <span className="srl-stat-value">{counts.all}</span>
        </div>
        <div className="srl-stat-divider"></div>
        <div className="srl-stat-item">
          <span className="srl-stat-label">Pending</span>
          <span className="srl-stat-value srl-stat-pending">{counts.pending}</span>
        </div>
        <div className="srl-stat-divider"></div>
        <div className="srl-stat-item">
          <span className="srl-stat-label">In Progress</span>
          <span className="srl-stat-value srl-stat-progress">{counts.in_progress}</span>
        </div>
        <div className="srl-stat-divider"></div>
        <div className="srl-stat-item">
          <span className="srl-stat-label">Completed</span>
          <span className="srl-stat-value srl-stat-completed">{counts.completed}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="srl-filters">
        <button 
          className={`srl-filter-btn ${filter === 'all' ? 'srl-filter-active' : ''}`} 
          onClick={() => setFilter('all')}
        >
          All
          <span className="srl-filter-count">{counts.all}</span>
        </button>
        <button 
          className={`srl-filter-btn ${filter === 'pending' ? 'srl-filter-active' : ''}`} 
          onClick={() => setFilter('pending')}
        >
          Pending
          <span className="srl-filter-count">{counts.pending}</span>
        </button>
        <button 
          className={`srl-filter-btn ${filter === 'in_progress' ? 'srl-filter-active' : ''}`} 
          onClick={() => setFilter('in_progress')}
        >
          In Progress
          <span className="srl-filter-count">{counts.in_progress}</span>
        </button>
        <button 
          className={`srl-filter-btn ${filter === 'completed' ? 'srl-filter-active' : ''}`} 
          onClick={() => setFilter('completed')}
        >
          Completed
          <span className="srl-filter-count">{counts.completed}</span>
        </button>
        <button 
          className={`srl-filter-btn ${filter === 'cancelled' ? 'srl-filter-active' : ''}`} 
          onClick={() => setFilter('cancelled')}
        >
          Cancelled
          <span className="srl-filter-count">{counts.cancelled}</span>
        </button>
      </div>

      {/* Table */}
      <div className="srl-table-wrapper">
        {filteredRequests.length === 0 ? (
          <div className="srl-empty-state">
            <Icons.Empty />
            <h3 className="srl-empty-title">No Requests Found</h3>
            <p className="srl-empty-text">
              {filter === 'all' 
                ? "You haven't made any service requests yet."
                : `You don't have any ${filter} service requests.`}
            </p>
            {filter === 'all' && (
              <button className="srl-btn-secondary" onClick={() => navigate('/dashboard/request-service')}>
                <Icons.Plus />
                Create Your First Request
              </button>
            )}
          </div>
        ) : (
          <table className="srl-table">
            <thead>
              <tr>
                <th className="srl-col-id">ID</th>
                <th className="srl-col-service">Service</th>
                <th className="srl-col-desc">Description</th>
                <th className="srl-col-status">Status</th>
                <th className="srl-col-date">Date</th>
                <th className="srl-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((request) => (
                <tr key={request.id} className="srl-table-row" onClick={() => navigate(`/dashboard/service-request/${request.id}`)}>
                  <td className="srl-col-id">
                    <span className="srl-id-badge">#{request.id}</span>
                  </td>
                  <td className="srl-col-service">
                    <span className="srl-service-name">
                      <Icons.Service />
                      {getServiceDisplayName(request)}
                    </span>
                  </td>
                  <td className="srl-col-desc">
                    <span className="srl-description">
                      {request.project_details.length > 80 
                        ? request.project_details.substring(0, 80) + '...' 
                        : request.project_details}
                    </span>
                  </td>
                  <td className="srl-col-status">
                    <span 
                      className="srl-status-badge"
                      style={{
                        background: getStatusBackground(request.status),
                        color: getStatusColor(request.status)
                      }}
                    >
                      <Icons.StatusDot />
                      {getStatusText(request.status)}
                    </span>
                  </td>
                  <td className="srl-col-date">
                    <span className="srl-date-text">
                      <Icons.Calendar />
                      {new Date(request.created_at).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </span>
                  </td>
                  <td className="srl-col-actions">
                    <button 
                      className="srl-action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/dashboard/service-request/${request.id}`);
                      }}
                    >
                      View Details
                      <Icons.ArrowRight />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ServiceRequestsList;